import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { getHotMovies, getGenreLabel, searchMovies } from "@/lib/movie";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [input, setInput] = useState(q);
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    setInput(q);
  }, [q]);

  const results = useMemo(() => searchMovies(input), [input]);
  const hot = getHotMovies(4);

  useSeo({
    title: q ? `搜索“${q}”的结果 - 最新电影` : "搜索电影 - 最新电影",
    description: q
      ? `在最新电影中搜索“${q}”，共找到 ${results.length} 部相关影片，涵盖多种类型的最新上映电影资讯与评分介绍。`
      : "输入片名、导演或类型关键词，快速在海量最新影片中找到你想看的那一部。",
    keywords: q ? `${q},搜索${q},${q}电影,最新电影搜索` : "电影搜索,搜索电影,最新电影,电影资讯",
    canonicalPath: "/search",
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setParams(kw ? { q: kw } : {});
  };

  return (
    <PageLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 py-10 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-background-400 mb-4">
            <Link to="/" className="hover:text-background-100 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">搜索</span>
          </nav>
          <h1 className="text-2xl md:text-3xl font-heading font-black text-background-50">
            电影搜索
          </h1>
          <p className="mt-2 text-sm text-background-300">
            支持按片名、导演或影片类型关键词搜索，结果会随输入实时更新
          </p>
          <form
            onSubmit={submit}
            className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-2xl"
          >
            <div className="flex-1 flex items-center bg-background-50 rounded-md pl-4">
              <i className="ri-search-line text-foreground-500 text-lg"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入片名、导演或类型，例如：科幻、逐光者…"
                className="flex-1 bg-transparent text-sm md:text-[15px] text-foreground-900 placeholder:text-foreground-400 px-3 py-3.5 focus:outline-none"
              />
              {input && (
                <button
                  type="button"
                  aria-label="清空关键词"
                  onClick={() => setInput("")}
                  className="px-3 text-foreground-400 hover:text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-circle-fill"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-7 py-3.5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 py-8 md:py-10">
        {input.trim() && (
          <p className="text-sm text-foreground-600 mb-6">
            {results.length > 0 ? (
              <>
                “<strong className="text-foreground-950">{input}</strong>” 的搜索结果，共找到{" "}
                <strong className="text-accent-600">{results.length}</strong> 部影片
              </>
            ) : (
              <>
                “<strong className="text-foreground-950">{input}</strong>” 没有匹配的影片
              </>
            )}
          </p>
        )}

        {!input.trim() && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-14 flex flex-col items-center text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h3 className="mt-4 text-foreground-900 font-semibold">输入关键词开始搜索</h3>
            <p className="mt-1.5 text-sm text-foreground-500">试试搜索“科幻”“爱情”或具体片名</p>
          </div>
        )}

        {input.trim() && results.length === 0 && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-14 flex flex-col items-center text-center px-4">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <h3 className="mt-4 text-foreground-900 font-semibold">没有找到相关影片</h3>
            <p className="mt-1.5 text-sm text-foreground-500">
              换个关键词试试，或看看下面这些热门电影
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5 mt-8 w-full text-left">
              {hot.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {results.map((movie) => (
              <MovieCard
                key={movie.id}
                movie={movie}
                isFavorite={isFavorite(movie.id)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-background-200">
          <p className="text-sm font-semibold text-foreground-700 mb-3">热门搜索</p>
          <div className="flex flex-wrap gap-2">
            {["动作", "爱情", "科幻", "悬疑", "动画", "喜剧"].map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => {
                  setInput(kw);
                  setParams({ q: kw });
                }}
                className="px-3.5 py-1.5 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-accent-100 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {kw}电影
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {hot.map((m) => (
              <Link
                key={m.id}
                to={`/detail/${m.id}`}
                className="px-3.5 py-1.5 rounded-full bg-primary-50 text-primary-700 text-sm hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {m.title}
                <span className="ml-1.5 text-secondary-700">{getGenreLabel(m.category)}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}